import { t } from "i18next";
import { Loader2 } from "lucide-react";
import { FC, useMemo, useState } from "react";

import clientLogo from "@/assets/client-logo.png";
import Icons from "@/components/icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/v2/avatar";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/v2/command";
import { useClientsInfinite } from "@/features/clients/api";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useInfiniteScroll } from "@/lib/hooks/useInfiniteScroll";

type ClientColumnProps = {
  tenantId: string | null;
  clientSearch: string;
  setClientSearch: (value: string) => void;
  selectedClientId?: string;
  onClientSelect: (clientId2: string, clientName: string) => void;
  onCreateClient: () => void;
  clientSearchInputRef?: React.RefObject<HTMLInputElement | null>;
};

const ClientColumn: FC<ClientColumnProps> = ({
  tenantId,
  clientSearch,
  setClientSearch,
  selectedClientId,
  onClientSelect,
  onCreateClient,
  clientSearchInputRef,
}) => {
  const [highlightedClientId, setHighlightedClientId] = useState<
    string | undefined
  >(undefined);
  const debouncedClientSearch = useDebounce(clientSearch, 300);
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useClientsInfinite({
    tenantId,
    search: debouncedClientSearch || undefined,
  });

  const clientsSearching = isLoading || (isFetching && !isFetchingNextPage);

  const clients = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );

  const { handleScroll } = useInfiniteScroll({
    onFetchMore: hasNextPage ? fetchNextPage : undefined,
    hasMore: Boolean(hasNextPage),
    isLoading,
    isFetching: isFetchingNextPage,
  });

  return (
    <div
      className="flex flex-col h-full"
      onPointerLeave={() => setHighlightedClientId(undefined)}
    >
      <Command
        shouldFilter={false}
        value={highlightedClientId ?? selectedClientId ?? ""}
        onValueChange={setHighlightedClientId}
        className="flex flex-col h-full"
      >
        <CommandInput
          ref={clientSearchInputRef}
          placeholder="Find Client..."
          value={clientSearch}
          onValueChange={setClientSearch}
          clearable
        />
        <CommandList className="flex-1 overflow-y-auto" onScroll={handleScroll}>
          {clientsSearching && (
            <CommandLoading
              label={t("common.loading", { defaultValue: "Loading..." })}
            >
              <div className="flex justify-center py-4">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </div>
            </CommandLoading>
          )}
          {!clientsSearching && clients.length === 0 && (
            <div
              data-slot="command-empty"
              className="py-6 text-center text-sm text-muted-foreground"
            >
              {clientSearch ? "No clients found" : "No clients, yet!"}
            </div>
          )}
          {!clientsSearching && clients.length > 0 && (
            <CommandGroup heading="Clients">
              <>
                {clients.map((client) => {
                  const isSelected = client.id2 === selectedClientId;
                  return (
                    <CommandItem
                      key={client.id2}
                      value={client.id2}
                      onSelect={() => onClientSelect(client.id2, client.name)}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Avatar className="size-5 shrink-0">
                          <AvatarImage
                            src={client.imagePath || clientLogo}
                            alt={client.name}
                          />
                          <AvatarFallback className="text-xs">
                            {client.name
                              ? client.name.charAt(0).toUpperCase()
                              : "C"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{client.name}</span>
                      </div>
                      {isSelected && (
                        <Icons.CheckIcon
                          className="shrink-0 text-foreground"
                          fontSize={16}
                        />
                      )}
                    </CommandItem>
                  );
                })}
                {isFetchingNextPage && (
                  <div className="py-2 text-center text-sm text-muted-foreground">
                    {t("common.loadingMore", {
                      defaultValue: "Loading more...",
                    })}
                  </div>
                )}
              </>
            </CommandGroup>
          )}
        </CommandList>
        <div className="border-t p-2 mt-auto">
          <CommandItem onSelect={onCreateClient} className="cursor-pointer">
            <Icons.AddIcon fontSize={16} className="text-muted-foreground" />
            <span>{t("table.clients.createButton")}</span>
          </CommandItem>
        </div>
      </Command>
    </div>
  );
};

export { ClientColumn };
export type { ClientColumnProps };
